import { eachDayOfInterval, endOfMonth, format, getDay, startOfMonth } from 'date-fns'
import { useMemo } from 'react'

import { SHORT_ISO_DATE_FORMAT } from '../../utils'

const YEAR_MONTHS = 12

type Day<T> = { date: string; color: string; data: T }
type Week<T> = Array<Day<T> | null>

type Input<T> = {
  year: number
  data: T[]
  dateFieldKey: keyof T
  getDayColor?: (day?: Partial<T>) => string | undefined
}

type Output<T> = {
  days: Day<T>[]
  months: Array<{
    monthDate: string
    monthName: string
    month: number
    weeks: Week<T>[]
  }>
}

export function useYearCalendar<T>({ year, data, dateFieldKey, getDayColor }: Input<T>): Output<T> {
  return useMemo(() => {
    const months = Array.from({ length: YEAR_MONTHS }, (_, monthIndex) => {
      const startDate = startOfMonth(new Date(year, monthIndex))
      const endDate = endOfMonth(startDate)
      const days = eachDayOfInterval({ start: startDate, end: endDate })

      const monthDays: Array<Day<T> | null> = []

      // fill first week days if needed
      const startDayOfWeek = getDay(startDate)
      for (let i = 0; i < startDayOfWeek; i++) {
        monthDays.push(null)
      }

      monthDays.push(
        ...days
          .map(date => format(date, SHORT_ISO_DATE_FORMAT))
          .map(date => {
            const dataItem = data.find(i => i?.[dateFieldKey] === date)
            if (!dataItem) {
              return { date, color: getDayColor?.({}) }
            }
            const color = getDayColor?.(dataItem)
            return { date, color, data: dataItem } as any // eslint-disable-line @typescript-eslint/no-explicit-any
          }),
      )

      // fill last week days if needed
      const remaining = 7 - (monthDays.length % 7)
      if (remaining < 7) {
        for (let i = 0; i < remaining; i++) {
          monthDays.push(null)
        }
      }

      const weeks: Week<T>[] = []
      for (let i = 0; i < monthDays.length; i += 7) {
        weeks.push(monthDays.slice(i, i + 7))
      }

      return {
        monthDate: format(startDate, 'yyyy-MM'),
        monthName: format(startDate, 'MMMM'),
        month: startDate.getMonth(),
        weeks,
      }
    })

    return {
      months,
      days: months
        .flatMap(month => month.weeks)
        .flatMap(week => week)
        .filter((day): day is Day<T> => day !== null),
    }
  }, [year, data, dateFieldKey, getDayColor])
}
